import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import { Dialog3 as Dialog } from '@st-operations/uikit/src/v1/index.js'
import {
  Button,
  BUTTON_THEME,
  CircularLoader,
  IconButton,
  Input,
  Switch,
} from '@st-operations/uikit/src/v2/index.js'

import './styles.scss'

import { INITIAL_FORM, MAX_SIZE, MIN_SIZE, generatePreviewId, validate } from './utils'

const ModalCreateIdSet = ({ isOpen, closeModal, create, progress }) => {
  const [form, setForm] = useState(INITIAL_FORM)
  const [error, setError] = useState(null)
  const [isSaving, setSaving] = useState(false)

  // Redrawn only when a rule changes. Keying it to the rules rather than to
  // every render keeps the example stable while the name is being typed, and
  // makes a rule change visible as a new example.
  const previewId = useMemo(() => generatePreviewId(form), [
    form.alphanumeric,
    form.starts_with_letter,
    form.size,
  ])

  // start each visit from a clean form rather than the last attempt's values
  useEffect(() => {
    if (isOpen) {
      setForm(INITIAL_FORM)
      setError(null)
      setSaving(false)
    }
  }, [isOpen])

  const handleChange = (name) => (value) => {
    setForm((prev) => ({ ...prev, [name]: value }))
    setError(null)
  }

  const toggle = (name) => () => {
    setForm((prev) => ({ ...prev, [name]: !prev[name] }))
    setError(null)
  }

  const handleCreate = async () => {
    const invalid = validate(form)
    if (invalid) return setError(invalid)

    setSaving(true)
    try {
      await create({
        name: form.name.trim(),
        description: form.description.trim(),
        alphanumeric: form.alphanumeric,
        starts_with_letter: form.starts_with_letter,
        size: Number(form.size),
      })
      closeModal()
    } catch (e) {
      // keep the modal open with the values intact so the name can be changed
      // and retried - the common failures are a name that is already taken and
      // a slug that collides with an existing mapping space
      setError(e?.message || 'Failed to create the ID set')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={isOpen} className="modal-create-id-set">
      <header className="modal-create-id-set__header">
        <h2>Create Segment ID Set</h2>
        <IconButton
          onClick={closeModal}
          aria-label="Close modal"
          iconProps={{
            icon: 'delete',
            type: 'fill',
            width: 16,
            height: 16,
            color: '#3d3d3d',
          }}
        />
      </header>

      <section className="modal-create-id-set__body">
        <p className="modal-create-id-set__intro">
          Defines an ID format for a partner that needs one. Creating it generates an ID for every
          taxonomy segment that already exists, and every segment created from now on gets one
          automatically.
        </p>

        <Input
          name="name"
          label="ID Set Name"
          description="Shown in every taxonomy's Segment IDs dropdown. Must be unique."
          value={form.name}
          onChange={handleChange('name')}
        />

        <Input
          name="description"
          label="Description"
          description="Optional. What this set is for, e.g. the partner that requires it."
          value={form.description}
          onChange={handleChange('description')}
        />

        <div className="modal-create-id-set__rules">
          <div className="modal-create-id-set__rule">
            <Switch checked={form.starts_with_letter} onChange={toggle('starts_with_letter')} />
            <div className="modal-create-id-set__rule-text">
              <span className="modal-create-id-set__rule-label">Starts with a letter</span>
              <span className="modal-create-id-set__rule-hint">
                First character is always a letter
              </span>
            </div>
          </div>

          <div className="modal-create-id-set__rule">
            <Switch checked={form.alphanumeric} onChange={toggle('alphanumeric')} />
            <div className="modal-create-id-set__rule-text">
              <span className="modal-create-id-set__rule-label">Alphanumeric</span>
              <span className="modal-create-id-set__rule-hint">
                {form.alphanumeric ? 'Letters and digits' : 'Digits only'}
              </span>
            </div>
          </div>
        </div>

        <div className="modal-create-id-set__size">
          <Input
            name="size"
            label="Number of characters"
            description={`${MIN_SIZE}-${MAX_SIZE} characters`}
            type="number"
            value={form.size}
            onChange={handleChange('size')}
          />
          <If condition={previewId}>
            <p className="modal-create-id-set__preview">
              Preview: <strong>{previewId}</strong>
            </p>
          </If>
        </div>

        <p className="modal-create-id-set__note">
          These rules are fixed once the set has generated its first ID - a set holding IDs of two
          different shapes can&apos;t be read back by the partner.
        </p>

        <If condition={isSaving}>
          <div className="modal-create-id-set__progress" role="status" aria-live="polite">
            <CircularLoader className="modal-create-id-set__spinner" />
            <span>
              <Choose>
                <When condition={progress > 0}>
                  Generated {progress.toLocaleString()} IDs so far. You can close this — it keeps
                  running in the background.
                </When>
                <Otherwise>
                  Generating IDs for every existing taxonomy segment. You can close this — it keeps
                  running in the background.
                </Otherwise>
              </Choose>
            </span>
          </div>
        </If>

        <If condition={error}>
          <p className="modal-create-id-set__error">{error}</p>
        </If>
      </section>

      <footer className="modal-create-id-set__footer">
        <div className="modal-create-id-set__actions">
          <Button onClick={closeModal} theme={BUTTON_THEME.SECONDARY} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={isSaving}>
            {isSaving ? 'Generating IDs...' : 'Create'}
          </Button>
        </div>
      </footer>
    </Dialog>
  )
}

export default ModalCreateIdSet

ModalCreateIdSet.propTypes = {
  isOpen: PropTypes.bool,
  closeModal: PropTypes.func.isRequired,
  create: PropTypes.func.isRequired,

  // how many existing segments the fill has got through, or null when it is not
  // running. The fill continues if this dialog is closed.
  progress: PropTypes.number,
}
